import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChatBubbleLeftRightIcon, PaperAirplaneIcon, UsersIcon, CheckCircleIcon,
  ArrowPathIcon, DocumentArrowUpIcon, TrashIcon, EyeIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import styles from './CampaignsPage.module.css';

const AUDIENCE_TYPES = [
  { value: 'all_vip',   label: 'All VIP' },
  { value: 'segment',   label: 'CRM Segment' },
  { value: 'lifecycle', label: 'Lifecycle' },
  { value: 'manual',    label: 'Manual list' },
];

const SEGMENTS = [
  { value: 'registered_no_vip', label: 'Registered — no VIP' },
  { value: 'vip_no_sales',      label: 'Active VIP — no purchase' },
  { value: 'expiring_30',       label: 'Expiring in 30 days' },
  { value: 'expiring_60',       label: 'Expiring in 31–60 days' },
  { value: 'churned',           label: 'Churned (expired)' },
];

const LIFECYCLES = [
  { value: 'new_signups', label: 'New signups (last N days)' },
  { value: 'expiring',    label: 'Expiring (next N days)' },
  { value: 'lapsed',      label: 'Lapsed / expired' },
];

const MEMBER_FIELDS = [
  { value: 'first_name', label: 'First name' },
  { value: 'last_name',  label: 'Last name' },
  { value: 'member_code',label: 'Member code' },
  { value: 'expiry_date',label: 'Expiry date' },
];

const STATUS_BADGE = {
  draft: 'badge-inactive', scheduled: 'badge-warning', sending: 'badge-warning',
  sent: 'badge-success', failed: 'badge-danger', canceled: 'badge-inactive',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [waConfigured, setWaConfigured] = useState(true);
  const [loading, setLoading] = useState(true);

  // compose state
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [paramMap, setParamMap] = useState({});           // { index: {source, value} }
  const [audienceType, setAudienceType] = useState('all_vip');
  const [segment, setSegment] = useState('expiring_30');
  const [lifecycle, setLifecycle] = useState('expiring');
  const [days, setDays] = useState(30);
  const [manualText, setManualText] = useState('');
  const [uploadedNumbers, setUploadedNumbers] = useState([]);
  const [respectConsent, setRespectConsent] = useState(false);
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'later'
  const [scheduledAt, setScheduledAt] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [showTemplates, setShowTemplates] = useState(false);
  const [detail, setDetail] = useState(null); // { campaign, recipients }

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId) || null,
    [templates, templateId]
  );

  const load = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([adminAPI.getCampaigns(), adminAPI.getWaTemplates()]);
      setCampaigns(c.campaigns || []);
      setTemplates(t.templates || []);
      setWaConfigured(t.whatsappConfigured !== false);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any campaign is actively sending.
  useEffect(() => {
    if (!campaigns.some((c) => c.status === 'sending')) return;
    const id = setInterval(() => { adminAPI.getCampaigns().then((r) => setCampaigns(r.campaigns || [])).catch(() => {}); }, 5000);
    return () => clearInterval(id);
  }, [campaigns]);

  const manualNumbers = useMemo(() => {
    const pasted = manualText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    return [...new Set([...pasted, ...uploadedNumbers])];
  }, [manualText, uploadedNumbers]);

  const audienceConfig = useCallback(() => {
    if (audienceType === 'segment')   return { segment };
    if (audienceType === 'lifecycle') return { lifecycle, days: Number(days) || 30 };
    if (audienceType === 'manual')    return { numbers: manualNumbers };
    return {};
  }, [audienceType, segment, lifecycle, days, manualNumbers]);

  const templateParams = useCallback(() => {
    const vars = selectedTemplate?.variables || [];
    return vars.map((v) => ({
      index: v.index,
      source: paramMap[v.index]?.source || 'static',
      value: paramMap[v.index]?.value || '',
    }));
  }, [selectedTemplate, paramMap]);

  const doPreview = async () => {
    setPreviewing(true); setError(''); setPreview(null);
    try {
      const r = await adminAPI.previewAudience({ audience_type: audienceType, audience_config: audienceConfig() });
      setPreview(r);
    } catch (e) { setError(e.message); }
    finally { setPreviewing(false); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await adminAPI.uploadAudience(fd);
      setUploadedNumbers(r.numbers || []);
      setOk(`${r.count} numbers loaded from ${file.name}`);
      setTimeout(() => setOk(''), 4000);
    } catch (e) { setError(e.message); }
    finally { e.target.value = ''; }
  };

  const resetForm = () => {
    setName(''); setTemplateId(''); setParamMap({}); setAudienceType('all_vip');
    setManualText(''); setUploadedNumbers([]); setRespectConsent(false);
    setScheduleMode('now'); setScheduledAt(''); setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setOk('');
    if (!name.trim()) return setError('Campaign name is required.');
    if (!templateId) return setError('Please choose a message template.');
    if (scheduleMode === 'later' && !scheduledAt) return setError('Please pick a schedule date/time.');
    if (audienceType === 'manual' && manualNumbers.length === 0) return setError('Add at least one phone number.');

    const payload = {
      name: name.trim(),
      template_id: templateId,
      template_params: templateParams(),
      audience_type: audienceType,
      audience_config: audienceConfig(),
      respect_consent: respectConsent,
      scheduled_at: scheduleMode === 'later' ? new Date(scheduledAt).toISOString() : null,
    };

    const verb = scheduleMode === 'later' ? 'schedule' : 'send now';
    if (!window.confirm(`Create and ${verb} campaign "${name}"?`)) return;

    setSubmitting(true);
    try {
      const { campaign } = await adminAPI.createCampaign(payload);
      if (scheduleMode === 'now') {
        await adminAPI.sendCampaign(campaign.id);
        setOk('Campaign created — sending started.');
      } else {
        setOk(`Campaign scheduled for ${fmtDate(payload.scheduled_at)}.`);
      }
      resetForm();
      load();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  const openDetail = async (c) => {
    setDetail({ campaign: c, recipients: null });
    try {
      const r = await adminAPI.getCampaignRecipients(c.id);
      setDetail({ campaign: c, recipients: r.recipients || [] });
    } catch { setDetail({ campaign: c, recipients: [] }); }
  };

  const cancelCampaign = async (c) => {
    if (!window.confirm(`Cancel scheduled campaign "${c.name}"?`)) return;
    try { await adminAPI.cancelCampaign(c.id); load(); } catch (e) { setError(e.message); }
  };
  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}" and its recipient log?`)) return;
    try { await adminAPI.deleteCampaign(c.id); load(); } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>WhatsApp Campaigns</h1>
          <p>Send template messages to your members over WhatsApp, schedule sends, and track delivery.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn-ghost" onClick={() => setShowTemplates((s) => !s)}>
            <DocumentArrowUpIcon style={{ width: 16, height: 16 }} /> Templates
          </button>
        </div>
      </div>

      {!waConfigured && (
        <div className={styles.warnBanner}>
          WhatsApp isn’t configured on the server yet (missing <code>WHATSAPP_TOKEN</code> / <code>WHATSAPP_PHONE_NUMBER_ID</code>).
          You can compose and schedule campaigns, but sends will fail until it’s set up.
        </div>
      )}

      {showTemplates && <TemplatesPanel templates={templates} onChange={load} />}

      <div className={styles.layout}>
        {/* ── Compose ── */}
        <div className={`card ${styles.composeCard}`}>
          <div className="card-title"><h2><ChatBubbleLeftRightIcon style={{ width: 18, height: 18, verticalAlign: '-3px', marginRight: 6 }} />New Campaign</h2></div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.group}>
              <label>Campaign name</label>
              <input className={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. April renewal reminder" />
            </div>

            <div className={styles.group}>
              <label>Message template</label>
              <select className={styles.input} value={templateId} onChange={(e) => { setTemplateId(e.target.value); setParamMap({}); }}>
                <option value="">— select an approved template —</option>
                {templates.filter((t) => t.status !== 'stale').map((t) => {
                  const car = carouselState(t);
                  return (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.language})
                      {car ? (car.ok ? ` \u2014 carousel, ${car.total} cards` : ' \u2014 carousel media missing') : ''}
                    </option>
                  );
                })}
              </select>
              {selectedTemplate?.body_preview && <div className={styles.templatePreview}>{selectedTemplate.body_preview}</div>}
              {(() => {
                const car = carouselState(selectedTemplate);
                if (!car) return null;
                return car.ok
                  ? <div className={styles.hint}>Carousel: {car.total} {car.kinds} card{car.total === 1 ? '' : 's'} ready.</div>
                  : <div className={styles.errorBanner}>Carousel media incomplete — {car.ready} of {car.total} {car.kinds} files are ready. Sync from Meta again; if it stays incomplete the creative is over WhatsApp&rsquo;s size limit and needs re-uploading on Meta.</div>;
              })()}
            </div>

            {(selectedTemplate?.variables || []).length > 0 && (
              <div className={styles.group}>
                <label>Template variables</label>
                {selectedTemplate.variables.map((v) => (
                  <div key={v.index} className={styles.paramRow}>
                    <span className={styles.paramTag}>{`{{${v.index}}}`}</span>
                    <select
                      className={styles.paramSelect}
                      value={paramMap[v.index]?.source || 'static'}
                      onChange={(e) => setParamMap((m) => ({ ...m, [v.index]: { ...m[v.index], source: e.target.value, value: '' } }))}
                    >
                      <option value="static">Static text</option>
                      <option value="field">Member field</option>
                    </select>
                    {(paramMap[v.index]?.source || 'static') === 'field' ? (
                      <select
                        className={styles.paramSelect}
                        value={paramMap[v.index]?.value || ''}
                        onChange={(e) => setParamMap((m) => ({ ...m, [v.index]: { ...m[v.index], value: e.target.value } }))}
                      >
                        <option value="">— field —</option>
                        {MEMBER_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                    ) : (
                      <input
                        className={styles.paramInput}
                        placeholder={v.example || `value for {{${v.index}}}`}
                        value={paramMap[v.index]?.value || ''}
                        onChange={(e) => setParamMap((m) => ({ ...m, [v.index]: { ...m[v.index], value: e.target.value } }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Audience */}
            <div className={styles.group}>
              <label>Audience</label>
              <div className={styles.segmented}>
                {AUDIENCE_TYPES.map((a) => (
                  <button type="button" key={a.value}
                    className={`${styles.segBtn} ${audienceType === a.value ? styles.segBtnActive : ''}`}
                    onClick={() => { setAudienceType(a.value); setPreview(null); }}>
                    {a.label}
                  </button>
                ))}
              </div>

              {audienceType === 'segment' && (
                <select className={styles.input} style={{ marginTop: 10 }} value={segment} onChange={(e) => setSegment(e.target.value)}>
                  {SEGMENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              )}

              {audienceType === 'lifecycle' && (
                <div className={styles.inlineRow} style={{ marginTop: 10 }}>
                  <select className={styles.input} value={lifecycle} onChange={(e) => setLifecycle(e.target.value)}>
                    {LIFECYCLES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                  {lifecycle !== 'lapsed' && (
                    <input className={styles.input} type="number" min="1" max="365" style={{ width: 90 }} value={days} onChange={(e) => setDays(e.target.value)} />
                  )}
                </div>
              )}

              {audienceType === 'manual' && (
                <div style={{ marginTop: 10 }}>
                  <textarea className={styles.textarea} rows={3} placeholder="Paste numbers, one per line (e.g. 03181819 or +9613181819)"
                    value={manualText} onChange={(e) => setManualText(e.target.value)} />
                  <div className={styles.uploadRow}>
                    <label className={styles.uploadBtn}>
                      <DocumentArrowUpIcon style={{ width: 15, height: 15 }} /> Upload CSV/XLSX
                      <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} hidden />
                    </label>
                    <span className={styles.hint}>{manualNumbers.length} number{manualNumbers.length !== 1 ? 's' : ''} ready</span>
                  </div>
                </div>
              )}
            </div>

            <label className={styles.consentRow}>
              <input type="checkbox" checked={respectConsent} onChange={(e) => setRespectConsent(e.target.checked)} />
              <span>Only send to members who opted in to WhatsApp</span>
            </label>

            {/* Preview */}
            <div className={styles.previewRow}>
              <button type="button" className="btn-ghost" onClick={doPreview} disabled={previewing}>
                <EyeIcon style={{ width: 15, height: 15 }} /> {previewing ? 'Checking…' : 'Preview audience'}
              </button>
              {preview && (
                <div className={styles.previewStats}>
                  <strong>{preview.count}</strong> recipients
                  <span className={styles.previewMuted}> · {preview.optedInCount} opted-in · {preview.optedOutCount} not</span>
                </div>
              )}
            </div>

            {/* Schedule */}
            <div className={styles.group}>
              <label>Delivery</label>
              <div className={styles.radioGroup}>
                <label className={styles.radio}>
                  <input type="radio" checked={scheduleMode === 'now'} onChange={() => setScheduleMode('now')} /> Send now
                </label>
                <label className={styles.radio}>
                  <input type="radio" checked={scheduleMode === 'later'} onChange={() => setScheduleMode('later')} /> Schedule
                </label>
                {scheduleMode === 'later' && (
                  <input className={styles.input} type="datetime-local" style={{ width: 'auto' }} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                )}
              </div>
            </div>

            {error && <div className={styles.errorBanner}>{error}</div>}
            {ok && <div className={styles.successBanner}><CheckCircleIcon style={{ width: 16, height: 16 }} /> {ok}</div>}

            <button type="submit" className="btn-primary" disabled={submitting} style={{ justifyContent: 'center' }}>
              {scheduleMode === 'later' ? <ClockIcon style={{ width: 16, height: 16 }} /> : <PaperAirplaneIcon style={{ width: 16, height: 16 }} />}
              {submitting ? 'Working…' : scheduleMode === 'later' ? 'Schedule campaign' : 'Send campaign'}
            </button>
          </form>
        </div>

        {/* ── History ── */}
        <div className={`card ${styles.historyCard}`}>
          <div className="card-title"><h2><UsersIcon style={{ width: 18, height: 18, verticalAlign: '-3px', marginRight: 6 }} />Campaigns</h2></div>

          {loading ? (
            [0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 92, borderRadius: 12, marginBottom: 10 }} />)
          ) : campaigns.length === 0 ? (
            <div className="empty-state"><ChatBubbleLeftRightIcon style={{ width: 28, height: 28 }} /><h3>No campaigns yet</h3><p>Create your first WhatsApp campaign on the left.</p></div>
          ) : (
            <div className={styles.campaignList}>
              {campaigns.map((c) => (
                <div key={c.id} className={styles.campaignItem}>
                  <div className={styles.campaignTop}>
                    <span className={styles.campaignName}>{c.name}</span>
                    <span className={`badge ${STATUS_BADGE[c.status] || 'badge-inactive'}`}>{c.status}</span>
                  </div>
                  <div className={styles.campaignSub}>
                    {c.template_name || '—'} · {c.audience_type.replace('_', ' ')}
                    {c.scheduled_at && c.status === 'scheduled' && <> · ⏰ {fmtDate(c.scheduled_at)}</>}
                  </div>
                  <div className={styles.analytics}>
                    <Stat label="Recipients" value={c.total_recipients} />
                    <Stat label="Sent" value={c.sent_count} tone="blue" />
                    <Stat label="Delivered" value={c.delivered_count} tone="green" />
                    <Stat label="Read" value={c.read_count} tone="gold" />
                    <Stat label="Failed" value={c.failed_count} tone="red" />
                  </div>
                  <div className={styles.campaignActions}>
                    <button className={styles.linkBtn} onClick={() => openDetail(c)}><EyeIcon style={{ width: 14, height: 14 }} /> Recipients</button>
                    {c.status !== 'draft' && c.sent_count + c.failed_count > 0 && (
                      <button className={styles.linkBtn} onClick={() => adminAPI.exportCampaignRecipients(c.id).then((b) => download(b, `campaign-${c.name}.csv`))}>Export</button>
                    )}
                    {c.status === 'scheduled' && <button className={styles.linkBtn} onClick={() => cancelCampaign(c)}>Cancel</button>}
                    <button className={`${styles.linkBtn} ${styles.danger}`} onClick={() => deleteCampaign(c)}><TrashIcon style={{ width: 14, height: 14 }} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {detail && <RecipientsModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={styles.stat}>
      <div className={`${styles.statValue} ${tone ? styles[`tone_${tone}`] : ''}`}>{value ?? 0}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

function RecipientsModal({ detail, onClose }) {
  const { campaign, recipients } = detail;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width: 640, maxWidth: '94vw' }} onClick={(e) => e.stopPropagation()}>
        <h3>{campaign.name} — recipients</h3>
        {recipients === null ? (
          <p>Loading…</p>
        ) : recipients.length === 0 ? (
          <p>No recipients recorded yet.</p>
        ) : (
          <div className="table-wrap" style={{ maxHeight: 380, overflowY: 'auto' }}>
            <table className="data-table">
              <thead><tr><th>Name</th><th>Phone</th><th>Status</th><th>Sent</th></tr></thead>
              <tbody>
                {recipients.map((r, i) => (
                  <tr key={i}>
                    <td>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td>{r.phone}</td>
                    <td><span className={`badge ${STATUS_BADGE[r.status] || 'badge-inactive'}`}>{r.status}</span></td>
                    <td>{r.sent_at ? new Date(r.sent_at).toLocaleString('en-GB') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function TemplatesPanel({ templates, onChange }) {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // The body and variables always come from Meta — a locally invented template
  // is rejected at send time, so there is nothing to type here but the name.
  const register = async () => {
    if (!name.trim()) return setErr('Template name is required.');
    setBusy(true); setErr('');
    try {
      await adminAPI.createWaTemplate({ name: name.trim(), language });
      setName(''); onChange();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true); setErr('');
    try {
      const r = await adminAPI.syncWaTemplates();
      onChange();
      alert(`Synced ${r.synced} approved templates from Meta.` + (r.stale ? `\n${r.stale} local template(s) no longer exist at Meta and were marked unavailable.` : ''));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { if (window.confirm('Delete this template?')) { await adminAPI.deleteWaTemplate(id); onChange(); } };

  return (
    <div className={`card ${styles.templatesPanel}`}>
      <div className="card-title">
        <h2>Message templates</h2>
        <button className="btn-ghost" onClick={sync} disabled={busy}><ArrowPathIcon style={{ width: 15, height: 15 }} /> Sync from Meta</button>
      </div>
      <div className={styles.tplLayout}>
        <div className={styles.tplForm}>
          <input className={styles.input} placeholder="Meta template name (exact)" value={name} onChange={(e) => setName(e.target.value)} />
          <div className={styles.inlineRow}>
            <select className={styles.input} value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="en">English (en)</option>
              <option value="ar">Arabic (ar)</option>
              <option value="en_US">English US (en_US)</option>
            </select>
          </div>
          <div className={styles.hint}>
            The name must match an approved template on the WhatsApp Business Account — its text and
            variables are read from Meta. Use “Sync from Meta” to pull in every approved template at once.
          </div>
          {err && <div className={styles.errorBanner}>{err}</div>}
          <button className="btn-primary" onClick={register} disabled={busy} style={{ justifyContent: 'center' }}>Verify &amp; add</button>
        </div>
        <div className={styles.tplList}>
          {templates.length === 0 ? <div className={styles.hint}>No templates yet. Register one or sync from Meta.</div> :
            templates.map((t) => (
              <div key={t.id} className={styles.tplItem}>
                <div>
                  <div className={styles.tplName}>
                    {t.name} <span className={styles.hint}>({t.language})</span>
                    {t.status === 'stale' && <span className={styles.hint}> — not on Meta</span>}
                    {(() => {
                      const car = carouselState(t);
                      if (!car) return null;
                      return <span className={styles.hint}>{car.ok ? ` — carousel (${car.total} ${car.kinds})` : ` — carousel media ${car.ready}/${car.total}`}</span>;
                    })()}
                  </div>
                  {t.body_preview && <div className={styles.tplBody}>{t.body_preview}</div>}
                </div>
                <button className={`${styles.linkBtn} ${styles.danger}`} onClick={() => remove(t.id)}><TrashIcon style={{ width: 14, height: 14 }} /></button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// Carousel cards carry their own image or video, mirrored from Meta when the
// template is synced. A card whose media could not be mirrored (missing, or
// over WhatsApp's 5MB image / 16MB video cap) makes the whole template
// unsendable, so it is called out before an audience is picked.
function carouselState(t) {
  const cards = t?.carousel?.cards;
  if (!Array.isArray(cards) || !cards.length) return null;
  const ready = cards.filter((c) => c.image_url).length;
  const kinds = [...new Set(cards.map((c) => (c.format || 'IMAGE').toLowerCase()))].join('/');
  return { total: cards.length, ready, kinds, ok: ready === cards.length };
}
function download(blob, filename) {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}
